import { camelizeKeys, decamelizeKeys } from "humps"
import Qs from "qs"
import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios"
import axiosRetry from "axios-retry"
import config from "config/config"
import {
	isLoginAttempt,
	isUnauthorizedOrForbidden,
	parseJSONorDefault,
} from "./utils"
import { logout, SessionDataStore } from "helpers/authUtils"
import { v4 as uuid } from "uuid"

export const REQUEST_CANCELLATION_MESSAGE =
	"Operation canceled due to new request."
const AUTHORIZATION_HEADER = "Authorization"
const CORRELATION_ID_HEADER = "X-Correlation-Id"
const APPLICATION_JSON = "application/json"

export const AXIOS_NUMBER_OF_RETRIES = 7
export const AXIOS_EXPONENTIAL_DELAY = 1
export const AXIOS_RETRY_DELAY = axiosRetry.exponentialDelay

const AXIOS_RETRY_CONFIG = {
	retries: AXIOS_NUMBER_OF_RETRIES,
	exponentialDelay: AXIOS_EXPONENTIAL_DELAY,
	retryDelay: AXIOS_RETRY_DELAY,
}

const EMPTY_RESPONSE = {
	data: {},
}

const {
	phronesis_api: {
		url,
		timeout: { timeout },
	},
} = config

const instance = axios.create({
	baseURL: url,
	timeout,
	transformResponse: [(data) => camelizeKeys(JSON.parse(data))],
	paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
})
axiosRetry(instance, AXIOS_RETRY_CONFIG)
instance.interceptors.request.use((request) => addCommonInterceptors(request))
instance.interceptors.response.use(
	(response) => response,
	(error) => addOnRejectedInterceptor(error)
)

const instanceWithCredentials = axios.create({
	baseURL: url,
	timeout,
	withCredentials: true,
	transformResponse: [(data) => camelizeKeys(JSON.parse(data))],
	paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
})
axiosRetry(instanceWithCredentials, AXIOS_RETRY_CONFIG)
instanceWithCredentials.interceptors.request.use((request) =>
	addCommonInterceptors(request)
)

const instanceWithCredentialsAcceptString = axios.create({
	baseURL: url,
	timeout,
	withCredentials: true,
	transformResponse: [(data) => camelizeKeys(parseJSONorDefault(data))],
	paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
})
axiosRetry(instanceWithCredentialsAcceptString, AXIOS_RETRY_CONFIG)
instanceWithCredentialsAcceptString.interceptors.request.use((request) =>
	addCommonInterceptors(request)
)

const instanceBlobWithCredentials = axios.create({
	baseURL: url,
	timeout,
	responseType: "blob",
	withCredentials: true,
	paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
})

axiosRetry(instanceBlobWithCredentials, AXIOS_RETRY_CONFIG)
instanceBlobWithCredentials.interceptors.request.use((request) =>
	addCommonInterceptors(request)
)
instanceBlobWithCredentials.interceptors.response.use(
	(response) => response,
	(error) => addOnRejectedInterceptor(error)
)

const addAuthTokenHeader = (request: InternalAxiosRequestConfig) => {
	const token = SessionDataStore.getInstance().getToken()
	if (token) {
		request.headers[AUTHORIZATION_HEADER] = `Bearer ${token}`
	}
}

const addCorrelationIdHeader = (request: InternalAxiosRequestConfig) => {
	request.headers[CORRELATION_ID_HEADER] = uuid()
}

const addCommonInterceptors = (request: any) => {
	addAuthTokenHeader(request)
	addCorrelationIdHeader(request)
	return request
}

const addOnRejectedInterceptor = (error: any) => {
	if (axios.isCancel(error)) {
		return EMPTY_RESPONSE
	}

	handleHTTPError(error)
	throw error
}

const handleHTTPError = (error: any) => {
	const message = error.response?.data?.message

	if (isLoginAttempt(error)) {
		return null
	}

	if (isUnauthorizedOrForbidden(error)) {
		return logout(true, message)
	}
	return null
}

export const httpGetRequestWithCredentials = <T>(
	endpoint: string,
	params: any = {},
	customTimeout: number = timeout,
	cancelToken?: any,
	headers: any = {},
	retries?: number
): Promise<T> => {
	return instanceWithCredentials
		.get(endpoint, {
			params: params,
			timeout: customTimeout,
			cancelToken: cancelToken,
			headers: headers,
			...(retries !== undefined && { "axios-retry": { retries } }),
		})
		.then(({ data }) => data)
}

export const httpGetRequestBlobWithCredentials = <T>(
	endpoint: string,
	params: any = {},
	customTimeout: number = timeout,
	cancelToken?: any,
	headers: any = {},
	retries?: number
): Promise<T> => {
	return instanceBlobWithCredentials
		.get(endpoint, {
			params: params,
			timeout: customTimeout,
			cancelToken: cancelToken,
			headers: headers,
			...(retries !== undefined && { "axios-retry": { retries } }),
		})
		.then(({ data }) => data)
}

export const httpGetWholeRequestWithCredentials = (
	endpoint: string,
	params: any = {},
	customTimeout: number = timeout,
	cancelToken?: any,
	headers: any = {},
	retries?: number
): Promise<AxiosResponse<any>> => {
	return instanceBlobWithCredentials
		.get(endpoint, {
			params: params,
			timeout: customTimeout,
			cancelToken: cancelToken,
			headers: headers,
			...(retries !== undefined && { "axios-retry": { retries } }),
		})
		.then((data) => data)
}

export const httpGetRequest = <T>(
	endpoint: string,
	params: any = {},
	customTimeout: number = timeout,
	cancelToken?: any,
	headers: any = {},
	retries?: number
): Promise<T> => {
	return instance
		.get(endpoint, {
			params: params,
			timeout: customTimeout,
			cancelToken: cancelToken,
			headers: headers,
			...(retries !== undefined && { "axios-retry": { retries } }),
		})
		.then(({ data }) => data)
}

export const httpPostRequest = (endpoint: string, data: any) =>
	instance
		.post(endpoint, decamelizeKeys(data), {
			headers: { "Content-Type": APPLICATION_JSON },
		})
		.then(({ data }) => data)

export const httpPostRequestWithCredentials = (endpoint: string, data: any) =>
	instanceWithCredentials
		.post(endpoint, decamelizeKeys(data), {
			headers: { "Content-Type": APPLICATION_JSON },
		})
		.then(({ data }) => data)

export const httpPutRequest = (
	endpoint: string,
	data: any,
	contentType: string = APPLICATION_JSON,
	isDecamelizeNeeded = true
) =>
	instance
		.put(endpoint, isDecamelizeNeeded ? decamelizeKeys(data) : data, {
			headers: { "Content-Type": contentType },
		})
		.then(({ data }) => data)

export const httpPutRequestWithCredentialsAcceptString = <T>(
	endpoint: string,
	data: any,
	contentType: string = APPLICATION_JSON,
	isDecamelizeNeeded = true
) =>
	instanceWithCredentialsAcceptString
		.put<T>(endpoint, isDecamelizeNeeded ? decamelizeKeys(data) : data, {
			headers: { "Content-Type": contentType },
		})
		.then(({ data }) => data)

export const httpDeleteRequest = (endpoint: string, params: any = {}) =>
	instance.delete(endpoint, { params }).then(({ data }) => data)

export const getCancelToken = () => axios.CancelToken.source()
